import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// Initial State
const initialState = {
    sessionCategories: [],
    currentSessionCategory: null,
    isLoading: false,
    error: null,
};

/* ==============================
   ASYNC THUNKS
============================== */

// Fetch all session categories
export const fetchSessionCategories = createAsyncThunk(
    'sessionCategory/fetchSessionCategories',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/session-categories');
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch session categories');
        }
    }
);

// Fetch single session category by ID
export const fetchSessionCategory = createAsyncThunk(
    'sessionCategory/fetchSessionCategory',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.get(`/session-categories/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch session category details');
        }
    }
);

// Create new session category
export const createSessionCategory = createAsyncThunk(
    'sessionCategory/createSessionCategory',
    async (formData, { rejectWithValue }) => {
        try {
            const response = await api.post('/session-categories', formData);
            return response.data.data;
        } catch (error) {
            if (error.response?.status === 422) {
                return rejectWithValue(error.response.data.errors);
            }
            return rejectWithValue(error.response?.data?.message || 'Failed to create session category');
        }
    }
);

// Update session category
export const updateSessionCategory = createAsyncThunk(
    'sessionCategory/updateSessionCategory',
    async ({ id, formData }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/session-categories/${id}`, formData);
            return response.data.data ?? response.data;
        } catch (error) {
            if (error.response?.status === 422) {
                return rejectWithValue(error.response.data.errors);
            }
            return rejectWithValue(error.response?.data?.message || 'Failed to update session category');
        }
    }
);

// Delete session category
export const deleteSessionCategory = createAsyncThunk(
    'sessionCategory/deleteSessionCategory',
    async (id, { rejectWithValue }) => {
        try {
            await api.delete(`/session-categories/${id}`);
            return id;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete session category');
        }
    }
);

/* ==============================
   SLICE
============================== */

const sessionCategorySlice = createSlice({
    name: 'sessionCategories',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setCurrentSessionCategory: (state, action) => {
            state.currentSessionCategory = action.payload;
        },
        clearCurrentSessionCategory: (state) => {
            state.currentSessionCategory = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // FETCH ALL
            .addCase(fetchSessionCategories.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchSessionCategories.fulfilled, (state, action) => {
                state.isLoading = false;
                state.sessionCategories = action.payload;
            })
            .addCase(fetchSessionCategories.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // FETCH SINGLE
            .addCase(fetchSessionCategory.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchSessionCategory.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentSessionCategory = action.payload;
            })
            .addCase(fetchSessionCategory.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // CREATE
            .addCase(createSessionCategory.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createSessionCategory.fulfilled, (state, action) => {
                state.isLoading = false;
                if (!action.payload) return;
                state.sessionCategories.unshift(action.payload);
                state.currentSessionCategory = action.payload;
            })
            .addCase(createSessionCategory.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // UPDATE
            .addCase(updateSessionCategory.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateSessionCategory.fulfilled, (state, action) => {
                state.isLoading = false;
                const updatedData = action.payload.data || action.payload;
                if (!updatedData) return;

                const index = state.sessionCategories.findIndex((f) => f.id === updatedData.id);
                if (index !== -1) {
                    state.sessionCategories[index] = updatedData;
                }
                if (state.currentSessionCategory?.id === updatedData.id) {
                    state.currentSessionCategory = updatedData;
                }
            })
            .addCase(updateSessionCategory.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // DELETE
            .addCase(deleteSessionCategory.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteSessionCategory.fulfilled, (state, action) => {
                state.isLoading = false;
                state.sessionCategories = state.sessionCategories.filter((f) => f.id !== action.payload);
                if (state.currentSessionCategory?.id === action.payload) {
                    state.currentSessionCategory = null;
                }
            })
            .addCase(deleteSessionCategory.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    },
});

export const { clearError, setCurrentSessionCategory, clearCurrentSessionCategory } = sessionCategorySlice.actions;
export default sessionCategorySlice.reducer;
